import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { verifyEmail, sendVerificationOTP } from "src/api/auth";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";
import AuthWrapper from "./AuthWrapper";
import AlertHelper from "src/helpers/alert";
import { setPendingUser } from "src/store/slices/auth";

const VerifyEmail = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const dispatch = useDispatch();
	const { tenant } = useSelector(({ auth }) => auth);
	const [otp, setOtp] = useState(['', '', '', '']);
	const [isSubmitting, setSubmitting] = useState(false);
	const [error, setError] = useState(null);
	const inputRefs = useRef([]);
	const isMounted = useRef(true);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	// Get data from location state
	const { firstName, lastName, email, role, token } = location.state || {};

	const handleOtpChange = (index, value) => {
		if (value.length > 1) {
			value = value.slice(0, 1);
		}
		
		const newOtp = [...otp];
		newOtp[index] = value;
		setOtp(newOtp);

		// Auto-focus next input
		if (value && index < 3) {
			inputRefs.current[index + 1]?.focus();
		}
	};

	const handleKeyDown = (index, e) => {
		if (e.key === 'Backspace' && !otp[index] && index > 0) {
			inputRefs.current[index - 1]?.focus();
		}
	};

	const safeSetState = (setter, value) => {
		if (isMounted.current) {
			setter(value);
		}
	};

	const onSubmit = async (e) => {
		e.preventDefault();
		console.log('[VerifyEmail] Starting verification process', { email, tenantId: tenant.id });
		
		if (!email) {
			console.error('[VerifyEmail] Email not found in state');
			AlertHelper.error("Email not found. Please start over.");
			return;
		}
		
		safeSetState(setSubmitting, true);
		try {
			safeSetState(setError, null);
			console.log('[VerifyEmail] Sending verification request', { 
				email: email.toLowerCase(),
				tenantId: tenant.id 
			});
			
			const response = await verifyEmail({ 
				email: email.toLowerCase(),
				otp: otp.join(''), 
				tenantId: tenant.id 
			});
			
			console.log('[VerifyEmail] Received response', response);
			
			if (response && response.success) {
				console.log('[VerifyEmail] Verification successful, storing state');
				
				// Store verification state and user data
				const userData = {
					firstName,
					lastName,
					email,
					role,
					token,
					isEmailVerified: true
				};
				
				// Store in Redux for current session
				dispatch(setPendingUser(userData));
				
				AlertHelper.success("Email verified successfully");
				
				console.log('[VerifyEmail] Navigating to set password page with data:', userData);
				// Navigate to set password
				navigate("/set-password", { 
					state: userData,
					replace: true
				});
			} else {
				console.error('[VerifyEmail] Verification failed', response);
				AlertHelper.error(response.message || "Verification failed. Please try again.");
			}
		} catch (err) {
			console.error('[VerifyEmail] Error during verification:', {
				error: err.message,
				stack: err.stack,
				email,
				response: err.response?.data,
				status: err.response?.status
			});

			// Handle different types of errors
			if (err.response?.data) {
				// Backend error with response
				const errorData = err.response.data;
				AlertHelper.error(errorData.message || "Verification failed. Please try again.");
			} else if (err.message === "Unauthorized") {
				// Handle unauthorized separately
				console.log('[VerifyEmail] Unauthorized error - continuing flow');
			} else {
				// Generic error
				AlertHelper.error("Failed to verify email. Please try again.");
			}
		} finally {
			safeSetState(setSubmitting, false);
		}
	};

	const handleResend = async () => {
		if (!email) {
			AlertHelper.error("Email not found. Please start over.");
			return;
		}
		safeSetState(setSubmitting, true);
		try {
			safeSetState(setError, null);
			await sendVerificationOTP({
				email,
				tenantId: tenant.id
			});
			AlertHelper.success("New OTP sent successfully");
		} catch (err) {
			console.error("Error resending OTP", err);
			AlertHelper.error(err.message || "Failed to resend OTP");
		} finally {
			safeSetState(setSubmitting, false);
		}
	};

	if (!email) {
		return (
			<AuthWrapper variant="standard" screen="verify-email">
				<div className="w-full max-w-md mx-auto text-center">
					<Alert severity="error">Email not found. Please start over.</Alert>
					<button
						onClick={() => navigate('/signup')}
						className="mt-4 px-4 py-2 bg-blue-700 text-white rounded-lg"
					>
						Go to Sign Up
					</button>
				</div>
			</AuthWrapper>
		);
	}

	return (
		<AuthWrapper variant="standard" screen="verify-email">
			<div className="w-full max-w-md mx-auto">
				<div className="flex flex-col items-center justify-center text-center space-y-2 mb-8">
					<div className="font-semibold text-3xl">
						<p>Email Verification</p>
					</div>
					<div className="flex flex-row text-sm font-medium text-gray-400">
						<p>We have sent a code to your email {email}</p>
					</div>
				</div>

				<form onSubmit={onSubmit}>
					<div className="flex flex-col space-y-8">
						<div className="flex flex-row items-center justify-between mx-auto w-full max-w-xs">
							{otp.map((digit, index) => (
								<div key={index} className="w-16 h-16">
									<input
										ref={el => inputRefs.current[index] = el}
										className="w-full h-full flex flex-col items-center justify-center text-center px-5 outline-none rounded-xl border border-gray-200 text-lg bg-white focus:bg-gray-50 focus:ring-1 ring-blue-700"
										type="text"
										maxLength={1}
										value={digit}
										onChange={(e) => handleOtpChange(index, e.target.value)}
										onKeyDown={(e) => handleKeyDown(index, e)}
									/>
								</div>
							))}
						</div>

						<div className="flex flex-col space-y-5">
							<div>
								<button
									type="submit"
									disabled={isSubmitting || otp.some(digit => !digit)}
									className="flex flex-row items-center justify-center text-center w-full border rounded-xl outline-none py-5 bg-blue-700 border-none text-white text-sm shadow-sm disabled:bg-blue-300"
								>
									{isSubmitting ? (
										<CircularProgress color="inherit" size={20} style={{ marginRight: 8 }} />
									) : null}
									Verify Account
								</button>
							</div>

							<div className="flex flex-row items-center justify-center text-center text-sm font-medium space-x-1 text-gray-500">
								<p>Didn't receive code?</p>
								<button
									type="button"
									onClick={handleResend}
									disabled={isSubmitting}
									className="flex flex-row items-center text-blue-600 disabled:text-blue-300"
								>
									Resend
								</button>
							</div>
						</div>
					</div>
				</form>
			</div>
		</AuthWrapper>
	);
};

export default VerifyEmail; 